import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminService } from 'core/services/adminService'
import { formatNumbers } from 'core/helpers/format-numbers'
import { routes } from 'core/routes/paths'

import c from './assets/sass/mines-admin.module.scss'

export const MinesAdmin = () => {
    const [view, setView] = useState('stats')

    return (
        <div className={c.component}>
            <div className={c.tabs}>
                <button
                    className={view === 'stats' ? c.tabActive : c.tab}
                    onClick={() => setView('stats')}
                >
                    📊 Статистика
                </button>
                <button
                    className={view === 'players' ? c.tabActive : c.tab}
                    onClick={() => setView('players')}
                >
                    🏆 Игроки
                </button>
                <button
                    className={view === 'config' ? c.tabActive : c.tab}
                    onClick={() => setView('config')}
                >
                    ⚙️ Настройки
                </button>
                <button
                    className={view === 'history' ? c.tabActive : c.tab}
                    onClick={() => setView('history')}
                >
                    📋 История
                </button>
            </div>

            {view === 'stats' && <StatsView />}
            {view === 'players' && <PlayersView />}
            {view === 'config' && <ConfigView />}
            {view === 'history' && <HistoryView />}
        </div>
    )
}

const StatsView = () => {
    const [stats, setStats] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        adminService.getMinesStats()
            .then(data => setStats(data.stats))
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [])

    if (loading) return <div className={c.loading}>Загрузка...</div>
    if (!stats) return <div className={c.loading}>Ошибка загрузки</div>

    const houseProfit = parseInt(stats.houseProfit)

    return (
        <>
            <div className={c.statsSection}>
                <h3 className={c.sectionTitle}>💰 За всё время</h3>
                <div className={c.statsGrid}>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Всего игр</span>
                        <span className={c.statsValue}>{formatNumbers(stats.totalGames)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Побед</span>
                        <span className={c.statsValueGreen}>{formatNumbers(stats.won)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Проигрышей</span>
                        <span className={c.statsValueRed}>{formatNumbers(stats.lost)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Оборот</span>
                        <span className={c.statsValue}>{formatNumbers(parseInt(stats.totalWagered))}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Выплачено</span>
                        <span className={c.statsValue}>{formatNumbers(parseInt(stats.totalPaid))}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Профит приложения</span>
                        <span className={houseProfit >= 0 ? c.statsValueGreen : c.statsValueRed}>
                            {houseProfit >= 0 ? '+' : ''}{formatNumbers(houseProfit)}
                        </span>
                    </div>
                </div>
            </div>

            <div className={c.statsSection}>
                <h3 className={c.sectionTitle}>📅 Сегодня</h3>
                <div className={c.statsGrid}>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Игр</span>
                        <span className={c.statsValue}>{formatNumbers(stats.today.games)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Игроков</span>
                        <span className={c.statsValue}>{formatNumbers(stats.today.uniquePlayers)}</span>
                    </div>
                    <div className={c.statsCard}>
                        <span className={c.statsLabel}>Оборот</span>
                        <span className={c.statsValue}>{formatNumbers(parseInt(stats.today.wagered))}</span>
                    </div>
                </div>
            </div>
        </>
    )
}

const PERIODS = [
    { id: '24h', label: '24 часа' },
    { id: '7d', label: '7 дней' },
    { id: '30d', label: '30 дней' },
]

const PlayersView = () => {
    const [period, setPeriod] = useState('24h')
    const [players, setPlayers] = useState([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 })
    const navigate = useNavigate()

    const loadPlayers = useCallback(async (page) => {
        setLoading(true)
        try {
            const data = await adminService.getMinesTopPlayers(period, page, 50)
            setPlayers(data.players || [])
            if (data.pagination) setPagination(data.pagination)
        } catch (e) {
            console.error('Failed to load top players:', e)
        } finally {
            setLoading(false)
        }
    }, [period])

    useEffect(() => {
        setPagination(p => ({ ...p, page: 1 }))
        loadPlayers(1)
    }, [loadPlayers])

    const handleUserClick = (userId) => {
        navigate(`${routes.adminUsers}?userId=${userId}`)
    }

    const offset = (pagination.page - 1) * pagination.limit

    return (
        <>
            <div className={c.filterTabs}>
                {PERIODS.map(p => (
                    <button
                        key={p.id}
                        className={period === p.id ? c.filterTabActive : c.filterTab}
                        onClick={() => setPeriod(p.id)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className={c.loading}>Загрузка...</div>
            ) : (
                <div className={c.statsSection}>
                    <h3 className={c.sectionTitle}>🏆 Топ игроков — {PERIODS.find(p => p.id === period)?.label}</h3>
                    <div className={c.topList}>
                        {players.map((p, i) => {
                            const profit = parseInt(p.netProfit)
                            return (
                                <div
                                    key={p.userId}
                                    className={c.topItem}
                                    onClick={() => handleUserClick(p.userId)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className={c.topName}>
                                        <span>{offset + i + 1}. </span>
                                        <span className={c.topUserLink}>{p.name}</span>
                                        <span className={c.topMeta}>
                                            {p.games} игр · {p.wins}W / {p.losses}L
                                        </span>
                                    </div>
                                    <span className={profit >= 0 ? c.topProfit : c.topProfitNeg}>
                                        {profit >= 0 ? '+' : ''}{formatNumbers(profit)}
                                    </span>
                                </div>
                            )
                        })}
                        {players.length === 0 && (
                            <div className={c.emptyState}>Нет данных за этот период</div>
                        )}
                    </div>
                    {pagination.pages > 1 && (
                        <div className={c.pagination}>
                            <button
                                onClick={() => { const p = pagination.page - 1; setPagination(prev => ({ ...prev, page: p })); loadPlayers(p) }}
                                disabled={pagination.page === 1}
                            >
                                ← Назад
                            </button>
                            <span>Страница {pagination.page} из {pagination.pages}</span>
                            <button
                                onClick={() => { const p = pagination.page + 1; setPagination(prev => ({ ...prev, page: p })); loadPlayers(p) }}
                                disabled={pagination.page >= pagination.pages}
                            >
                                Вперед →
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

const Tooltip = ({ text }) => (
    <span className={c.tooltip}>
        <span className={c.tooltipIcon}>?</span>
        <span className={c.tooltipText}>{text}</span>
    </span>
)

const CONFIG_FIELDS = [
    { key: 'houseEdge', label: 'RTP (отдача)', step: '0.01',
      hint: 'Доля возврата игроку. 0.20 = 20% забирает приложение, 80% получает игрок. Чем выше — тем больше забирает приложение.' },
    { key: 'profitLimit', label: 'Лимит профита (монет)',
      hint: 'Максимальный чистый выигрыш игрока за указанное окно времени. При достижении — новые игры блокируются.' },
    { key: 'profitLimitWindowHours', label: 'Окно лимита (часов)',
      hint: 'Скользящее окно для подсчёта профита. Например 2 часа — считается чистый выигрыш за последние 2 часа.' },
    { key: 'rateLimitReveals', label: 'Лимит ходов в минуту',
      hint: 'Максимальное количество открытий клеток в минуту на одного игрока. Защита от бот-спама.' },
    { key: 'rateLimitWindowSec', label: 'Окно лимита ходов (сек)',
      hint: 'Временное окно для подсчёта ходов. По умолчанию 60 секунд.' },
    { key: 'winrateThreshold', label: 'Порог винрейта', step: '0.01',
      hint: 'Если процент побед игрока выше этого значения — включается скрытый кулдаун. 0.9 = 90% побед.' },
    { key: 'winrateWindow', label: 'Окно винрейта (игр)',
      hint: 'Количество последних игр для расчёта винрейта. 20 = анализируются последние 20 завершённых игр.' },
    { key: 'winrateCooldownMin', label: 'Кулдаун мин (сек)',
      hint: 'Минимальное время блокировки при срабатывании детекции винрейта. Игрок видит "Подождите".' },
    { key: 'winrateCooldownMax', label: 'Кулдаун макс (сек)',
      hint: 'Максимальное время блокировки. Реальный кулдаун — случайное число между мин и макс (сложнее отследить).' },
    { key: 'bet1Amount', label: 'Ставка 1 — сумма',
      hint: 'Размер первой (лёгкой) ставки в монетах.' },
    { key: 'bet1Mines', label: 'Ставка 1 — кол-во мин',
      hint: 'Количество мин при первой ставке. Больше мин = выше риск = выше множители.' },
    { key: 'bet2Amount', label: 'Ставка 2 — сумма',
      hint: 'Размер второй (средней) ставки в монетах.' },
    { key: 'bet2Mines', label: 'Ставка 2 — кол-во мин',
      hint: 'Количество мин при второй ставке.' },
    { key: 'bet3Amount', label: 'Ставка 3 — сумма',
      hint: 'Размер третьей (сложной) ставки в монетах.' },
    { key: 'bet3Mines', label: 'Ставка 3 — кол-во мин',
      hint: 'Количество мин при третьей ставке.' },
    { key: 'staleGameMinutes', label: 'Таймаут игры (мин)',
      hint: 'Через сколько минут незавершённая игра автоматически отменяется и ставка возвращается.' },
    { key: '_separator_force', label: '🎯 Динамическая сложность', type: 'separator' },
    { key: 'forceLossEnabled', label: 'Force Loss вкл', type: 'toggle',
      hint: 'Принудительные проигрыши на высоких ставках. ВЫКЛ = честная игра. ВКЛ = контроль глубоких выигрышей.' },
    { key: 'forceLossLowPower', label: 'Сила — мин. ставка', step: '0.1',
      hint: 'Сила force loss на минимальной ставке (100). 2.0 ≈ 1/30 для 6 клеток. 0 = отключено. Лайтовая версия.' },
    { key: 'forceLossMidPower', label: 'Сила — средняя ставка', step: '0.1',
      hint: 'Сила force loss на средней ставке (500). 2.5 ≈ 1/400 для 6 клеток. 0 = отключено. Больше = жёстче.' },
    { key: 'forceLossHighPower', label: 'Сила — высокая ставка', step: '0.1',
      hint: 'Сила force loss на высокой ставке (1000). 2.1 ≈ 1/800 для 6 клеток. 0 = отключено. Больше = жёстче.' },
    { key: 'forceLossMinDepth', label: 'Мин. глубина force loss',
      hint: 'Force loss начинает работать после N открытых клеток. 1 = со второго хода. 0 = с первого. Первые N ходов безопасны от force.' },
]

const ConfigView = () => {
    const [draft, setDraft] = useState(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)

    useEffect(() => {
        adminService.getMinesConfig()
            .then(data => setDraft(data.config))
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [])

    const handleSave = async () => {
        setSaving(true)
        setSaved(false)
        try {
            const data = await adminService.updateMinesConfig(draft)
            setDraft(data.config)
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
        } catch (e) {
            console.error('Save config error:', e)
            alert('Ошибка сохранения: ' + (e.response?.data?.error || e.message))
        } finally {
            setSaving(false)
        }
    }

    const update = (key, value) => {
        if (typeof value === 'boolean') {
            setDraft(prev => ({ ...prev, [key]: value }))
        } else {
            setDraft(prev => ({ ...prev, [key]: parseFloat(value) || value }))
        }
    }

    if (loading) return <div className={c.loading}>Загрузка...</div>
    if (!draft) return <div className={c.loading}>Ошибка загрузки</div>

    return (
        <div className={c.configSection}>
            <h3 className={c.sectionTitle}>⚙️ Параметры игры</h3>
            <div style={{ fontSize: 11, color: 'rgba(76, 175, 80, 0.7)', marginBottom: 12 }}>
                ⚡ Все настройки применяются мгновенно без перезапуска сервера
            </div>
            <div className={c.configGrid}>
                {CONFIG_FIELDS.map(f => {
                    if (f.type === 'separator') {
                        return (
                            <div key={f.key} className={c.configRow} style={{
                                borderTop: '1px solid rgba(255,255,255,0.1)',
                                paddingTop: 12,
                                marginTop: 8,
                                gridColumn: '1 / -1'
                            }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#ff9800' }}>{f.label}</span>
                            </div>
                        )
                    }
                    return (
                        <div key={f.key} className={c.configRow}>
                            <span className={c.configLabel}>
                                {f.label}
                                {f.hint && <Tooltip text={f.hint} />}
                            </span>
                            {f.type === 'toggle' ? (
                                <button
                                    style={{
                                        padding: '6px 16px',
                                        borderRadius: 6,
                                        border: 'none',
                                        fontWeight: 600,
                                        fontSize: 12,
                                        cursor: 'pointer',
                                        color: '#fff',
                                        background: draft[f.key] ? '#4CAF50' : '#f44336',
                                    }}
                                    onClick={() => update(f.key, draft[f.key] ? false : true)}
                                >
                                    {draft[f.key] ? 'ВКЛ' : 'ВЫКЛ'}
                                </button>
                            ) : (
                                <input
                                    type="number"
                                    step={f.step || '1'}
                                    className={c.configInput}
                                    value={draft[f.key] ?? ''}
                                    onChange={(e) => update(f.key, e.target.value)}
                                />
                            )}
                        </div>
                    )
                })}
            </div>
            <button className={c.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Сохранение...' : saved ? '✅ Сохранено!' : 'Сохранить настройки'}
            </button>
        </div>
    )
}

const STATUS_LABELS = {
    WON: 'Победа',
    LOST: 'Проигрыш',
    ACTIVE: 'Активна',
    CANCELLED: 'Отменена',
}

const STATUS_CLASSES = {
    WON: cls => cls.statusWon,
    LOST: cls => cls.statusLost,
    ACTIVE: cls => cls.statusActive,
    CANCELLED: cls => cls.statusCancelled,
}

const formatDate = (dateString) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit',
        hour: '2-digit', minute: '2-digit',
    })
}

const FILTERS = [
    { id: '', label: 'Все' },
    { id: 'WON', label: '✅ Победы' },
    { id: 'LOST', label: '❌ Проигрыши' },
    { id: 'ACTIVE', label: '🟡 Активные' },
    { id: 'CANCELLED', label: '🚫 Отменены' },
]

const HistoryView = () => {
    const [games, setGames] = useState([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 })
    const [statusFilter, setStatusFilter] = useState('')
    const navigate = useNavigate()

    const loadGames = useCallback(async (page, status) => {
        try {
            setLoading(true)
            const params = { page, limit: pagination.limit }
            if (status) params.status = status
            const data = await adminService.getMinesGames(params)
            setGames(data.games || [])
            setPagination(data.pagination)
        } catch (e) {
            console.error('Failed to load games:', e)
        } finally {
            setLoading(false)
        }
    }, [pagination.limit])

    useEffect(() => {
        loadGames(pagination.page, statusFilter)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, statusFilter])

    const handleFilterChange = (status) => {
        setStatusFilter(status)
        setPagination(p => ({ ...p, page: 1 }))
    }

    const handleUserClick = (userId) => {
        navigate(`${routes.adminUsers}?userId=${userId}`)
    }

    return (
        <>
            <div className={c.filterTabs}>
                {FILTERS.map(f => (
                    <button
                        key={f.id}
                        className={statusFilter === f.id ? c.filterTabActive : c.filterTab}
                        onClick={() => handleFilterChange(f.id)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {loading && games.length === 0 ? (
                <div className={c.loading}>Загрузка...</div>
            ) : (
                <>
                    <div className={c.list}>
                        {games.map(game => {
                            const statusCls = (STATUS_CLASSES[game.status] || STATUS_CLASSES.CANCELLED)(c)
                            return (
                                <div key={game.id} className={c.item}>
                                    <span
                                        className={c.itemUserLink}
                                        onClick={() => handleUserClick(game.userId)}
                                    >
                                        {game.username}
                                    </span>
                                    <span className={`${c.itemStatus} ${statusCls}`}>
                                        {STATUS_LABELS[game.status] || game.status}
                                    </span>
                                    <div className={c.itemMeta}>
                                        <span>
                                            Ставка: <span className={c.metaVal}>{formatNumbers(parseInt(game.bet))}</span>
                                        </span>
                                        <span>
                                            Мин: <span className={c.metaVal}>{game.minesCount}</span>
                                        </span>
                                        <span>
                                            Открыто: <span className={c.metaVal}>{game.revealedCount}</span>
                                        </span>
                                        {game.status === 'WON' && game.payout && (
                                            <span>
                                                Выплата: <span className={c.metaVal} style={{ color: '#4CAF50' }}>
                                                    {formatNumbers(parseInt(game.payout))}
                                                </span>
                                            </span>
                                        )}
                                        <span className={c.metaDate}>{formatDate(game.createdAt)}</span>
                                    </div>
                                </div>
                            )
                        })}
                        {games.length === 0 && (
                            <div className={c.emptyState}>Нет игр</div>
                        )}
                    </div>

                    {pagination.pages > 1 && (
                        <div className={c.pagination}>
                            <button
                                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                                disabled={pagination.page === 1}
                            >
                                ← Назад
                            </button>
                            <span>Страница {pagination.page} из {pagination.pages}</span>
                            <button
                                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                                disabled={pagination.page >= pagination.pages}
                            >
                                Вперед →
                            </button>
                        </div>
                    )}
                </>
            )}
        </>
    )
}
